// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { Input } from '@shared/ui-kit/components/input/input';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { forwardRef, type ComponentProps, type FocusEventHandler } from 'react';
import { PatternFormat } from 'react-number-format';
import { useDateInput, type DateInputGranularity } from './use-date-input';

export type { DateInputGranularity };

export type DateInputProps = Omit<
  ComponentProps<'input'>,
  'value' | 'onChange' | 'type' | 'min' | 'max' | 'defaultValue'
> & {
  value?: Date | string | null;
  onChange?: (value: string) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  minDate?: Date | string;
  maxDate?: Date | string;
  error?: boolean;
  /** `date` (default) emits `YYYY-MM-DD`; `datetime` emits `YYYY-MM-DDTHH:mm:ss`. */
  granularity?: DateInputGranularity;
};

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      value,
      onChange,
      onBlur,
      minDate,
      maxDate,
      granularity = 'date',
      className,
      error,
      disabled,
      readOnly,
      required,
      ...props
    },
    ref,
  ) => {
    const { displayValue, handleBlur, handleValueChange, mask, placeholder } =
      useDateInput({
        value,
        onChange,
        onBlur,
        minDate,
        maxDate,
        granularity,
      });

    return (
      <div className="relative w-full">
        <PatternFormat
          customInput={Input}
          getInputRef={ref}
          format={mask}
          mask="_"
          value={displayValue}
          onValueChange={handleValueChange}
          onBlur={handleBlur}
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          className={classNames('pr-10', className)}
          error={error}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          valueIsNumericString={false}
          {...props}
        />

        <AppIcon
          icon="Calendar"
          size="base"
          color="disabled"
          aria-hidden
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2"
        />
      </div>
    );
  },
);

DateInput.displayName = 'DateInput';
